import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useSalesOrder } from '@/api/rpc/hooks/accounts-receivable/useSalesOrders';
import {
  ShoppingCartIcon,
  ArrowSquareOutIcon,
  PackageIcon,
} from '@phosphor-icons/react';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { Badge } from '@/components/ui/badge';

interface SalesOrderPreviewProps {
  entityType: SystemEntity; // Required by preview component registry but not used - type is always AR_SALES_ORDER
  entityId: string;
  className?: string;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  fulfilled: 'Fulfilled',
  closed: 'Closed',
};

const STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  pending: 'secondary',
  in_progress: 'default',
  fulfilled: 'default',
  closed: 'outline',
};

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return 'N/A';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return 'N/A';

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

export function SalesOrderPreview({
  entityId,
  className,
}: SalesOrderPreviewProps) {
  const {
    data: salesOrderData,
    isLoading,
    error,
    lineItems,
  } = useSalesOrder(entityId);

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <ShoppingCartIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Sales Order</h4>
        </div>
        <p className="text-xs text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (error || !salesOrderData?.salesOrder) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <ShoppingCartIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Sales Order</h4>
        </div>
        <p className="text-xs text-muted-foreground">
          Unable to load sales order details
        </p>
      </div>
    );
  }

  const salesOrder = salesOrderData.salesOrder;

  // Use customer PO number if available, otherwise internal ID
  const orderIdentifier =
    salesOrder.customerPurchaseOrderNumber || `SO-${salesOrder.internalId}`;

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <ShoppingCartIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">{orderIdentifier}</h4>
      </div>

      <div className="space-y-2 text-xs mb-3">
        <div className="flex items-center justify-between">
          <Badge
            variant={STATUS_VARIANTS[salesOrder.status] || 'outline'}
            className="text-xs"
          >
            {STATUS_LABELS[salesOrder.status] || salesOrder.status}
          </Badge>
        </div>

        {salesOrder.totalAmount && (
          <div>
            <span className="text-muted-foreground">Total: </span>
            <span className="font-medium">
              {formatCurrencyValue(
                salesOrder.totalAmount,
                salesOrder.currencyCode || 'USD',
              )}
            </span>
          </div>
        )}

        <div>
          <span className="text-muted-foreground">Order Date: </span>
          <span className="font-medium">
            {formatDateOnlyString(salesOrder.orderDate)}
          </span>
        </div>

        {lineItems.length > 0 && (
          <div className="flex items-center gap-1">
            <PackageIcon className="size-3 text-muted-foreground" />
            <span className="text-muted-foreground">Line Items: </span>
            <span className="font-medium">{lineItems.length}</span>
          </div>
        )}
      </div>

      <div className="mt-3">
        <a
          href={`/accounts-receivable/sales-orders/${salesOrder.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-full px-3 py-1.5 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
        >
          <ArrowSquareOutIcon className="size-3 mr-1.5" />
          View Details
        </a>
      </div>
    </div>
  );
}
